<script>
import { iFilter, showToast } from '@inscribe/signal';
import { mapActions, mapGetters, mapMutations } from 'vuex';

/**
 * this components shows a list of tags and send an event when the selection changes
 * It offers the option to provide a trigger element or a method to open a selector by ID
 * Internally it displays the list in the i-filter component
 */
export default {
  components: { iFilter },
  props: {
    title: {
      type: String,
      default: 'Select Tags',
    },
    label: {
      type: String,
      default: null,
    },
    placeholder: {
      type: String,
      default: 'Search or Create',
    },
    document: {
      type: Object,
      default: null,
    },
    triggerLocator: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      triggerContainer: null,
      componentId: crypto.randomUUID(),
      selectedTags: [],
    };
  },
  computed: {
    ...mapGetters(['getTagsList', 'getDocumentById']),
    hasCustomTrigger() {
      return !!this.$slots.trigger;
    },
    tags() {
      return this.getTagsList.map((tagObject) => {
        return {
          label: tagObject.value,
        };
      });
    },
    customTrigger() {
      return `#tags-selector-${this.componentId} .trigger-container ${this.triggerLocator}`;
    },
  },
  watch: {
    'document.tags': {
      deep: true,
      immediate: true,
      handler: function () {
        this.presetTags();
      },
    },
  },
  mounted() {
    this.presetTags();
  },
  methods: {
    ...mapMutations(['addTags']),
    ...mapActions([
      'updateDocument',
      'fetchAndCacheDocument',
      'fetchAndCacheFirstPageOfSummaryDocumentsForCustomerUUID',
    ]),
    presetTags() {
      if (this.document?.tags) {
        this.selectedTags = this.document.tags.map((tag) => tag.value);
      }
    },
    onTagsSelected(selectedObject) {
      this.selectedTags = selectedObject.selection.map((tag) => tag.label);
      this.onSelectionChanged();
    },
    onTagCreateButtonClicked(tag) {
      this.addTags([
        {
          value: tag,
        },
      ]);
      this.selectedTags.push(tag);
      this.onSelectionChanged();
    },
    onSelectionChanged() {
      this.$emit('input', this.selectedTags);
      if (this.document) {
        this.updateDocTags();
      }
    },
    updateDocTags() {
      // For backwards compatibility, update the deprecated document store value
      const docInDeprecatedStore =
        this.getDocumentById(this.document.id) || this.document;
      docInDeprecatedStore.tags = this.selectedTags.map(
        (tag) =>
          this.$store.state.tags.list.find(({ value }) => value === tag) || {
            value: tag,
          },
      );

      this.updateDocument({
        document: this.document,
        data: {
          id: this.document.id,
          customer_id: this.document.customer_id,
          tags: this.selectedTags.map((tag) => {
            return { text: tag };
          }),
        },
      }).then(
        () => {
          // refetch the document queries so the store gets updated
          this.fetchAndCacheDocument({
            customerId: this.document.customer_id,
            documentId: this.document.id,
          });
          this.fetchAndCacheFirstPageOfSummaryDocumentsForCustomerUUID(
            this.document.customer_id,
          );
          showToast({
            text: 'Tags successfully updated.',
            timeout: 2000,
          });
        },
        () => {
          showToast({
            text: 'Error updating the tags.',
            isError: true,
          });
        },
      );
    },
  },
};
</script>

<template>
  <div :id="`tags-selector-${componentId}`" class="tags-selector">
    <div ref="triggerContainer" class="trigger-container">
      <slot name="trigger"></slot>
    </div>
    <i-filter
      :label="label"
      :title="title"
      :options="tags"
      :search-input-placeholder="placeholder"
      :custom-trigger="hasCustomTrigger ? customTrigger : undefined"
      :searchable="true"
      :max-selected-extended-list="2"
      :value="selectedTags"
      :show-selected-list="false"
      :show-empty-create-button="true"
      empty-create-button-label="Create `${value}` tag"
      @selection-changed="onTagsSelected"
      @create-button-clicked="onTagCreateButtonClicked"
    />
  </div>
</template>
